import * as React from "react";
import { observer } from "mobx-react";
import { ToggleContainerProps, ToggleContainerFlexModuleModelResult } from "../typings";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { UitkCard, UitkCardContentSection } from "@egds/react-core/cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "@egds/react-core/layout-flex";
import { UitkIcon, UitkIconSize } from "@egds/react-core/icons";
import { UitkSpacing } from "@egds/react-core/spacing";
import { UitkHeading, UitkText } from "@egds/react-core/text";
import { getExtraClassNameBasedOnParameters } from "components/flexComponents/SimpleContainer/utilityStyles";
import { ToggleAllContainerContext, ToggleContainerState } from "./ToggleAllContainer";
import { hasRenderableChildren } from "src/stores/ExperienceTemplateStore/shouldRender";
import { RegionChildrenWithGridContainer } from "src/utils/RegionUtils";
import { IntersectionTracker, useClickTracker } from "@shared-ui/clickstream-analytics-context";

export interface ToggleSingleContainerContext {
  modulesHaveBorder?: boolean;
}

const defaultContext = {
  modulesHaveBorder: false,
};

export const ToggleSingleContainerContext = React.createContext<ToggleSingleContainerContext>(defaultContext);

export const ToggleSingleContainer = observer(
  ({ context, blossomComponent, flexModuleModelStore, templateComponent }: ToggleContainerProps) => {
    /* istanbul ignore next */
    if (!templateComponent || !blossomComponent) {
      return null;
    }

    const {
      metadata: { id, name: containerName = "" },
    } = templateComponent;
    const { rfrrID } = templateComponent.config;
    const impressionLinkName = rfrrID + " Link";
    const impressionRfrrID = `${rfrrID}.Impression`;
    const model = (flexModuleModelStore.getModel(id) as ToggleContainerFlexModuleModelResult) || null;

    if (!model) {
      return null;
    }

    const {
      mobileWidth,
      title,
      innerTitle,
      tabletWidth,
      desktopWidth,
      collapsed,
      hasBorder: modulesHaveBorder,
    } = model;

    if (!hasRenderableChildren(templateComponent, flexModuleModelStore)) {
      return null;
    }
    const commonClassNames = [getExtraClassNameBasedOnParameters(model, containerName)]
      .filter((classes) => classes !== "")
      .join(" ");

    const ContainerContent = (containerProps: { includeTitle: boolean; classNames: string }) => (
      <div className={` ${commonClassNames} ${containerProps.classNames} `} role="region">
        {title && (
          <UitkSpacing>
            <UitkHeading tag="h2" size={3} className={`${!containerProps.includeTitle && "is-visually-hidden"} `}>
              {title}
            </UitkHeading>
          </UitkSpacing>
        )}
        {innerTitle && (
          <UitkSpacing padding={{ inlinestart: "three" }}>
            <UitkHeading tag="h3" size={3}>
              {innerTitle}
            </UitkHeading>
          </UitkSpacing>
        )}
        <RegionChildrenWithGridContainer templateComponent={templateComponent} blossomComponent={blossomComponent} />
      </div>
    );

    const toggleAllContainerContext = React.useContext(ToggleAllContainerContext);
    const { collapseAll, toggleCollapseAll } = toggleAllContainerContext;
    const [isVisible, setIsVisible] = React.useState(!collapsed);
    const trackProgressiveDisclosure = useClickTracker();
    const toggle = React.useCallback(() => {
      setIsVisible(!isVisible);

      const id = `${rfrrID}.${isVisible ? "closed" : "opened"}`;
      const description = `${rfrrID} ${isVisible ? "collapsed" : "expanded"}`;
      trackProgressiveDisclosure(id, description);
      toggleCollapseAll(ToggleContainerState.NEITHER);
    }, [isVisible]);
    const normalTitle = (
      <UitkText size={400} weight="bold" theme="default">
        {title}
      </UitkText>
    );
    const expandoTitle = (
      <UitkHeading tag="h2" size={4}>
        {title}
      </UitkHeading>
    );
    const expandoKeyboardPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        toggle();
      }
    };
    const toggleSingleContainerView = (
      <Viewport>
        {mobileWidth === "hidden" ? (
          <ViewSmall />
        ) : (
          <ViewSmall>
            <UitkSpacing padding={{ block: rfrrID ? "two" : "three" }}>
              <div className="SimpleContainer ToggleContainer">
                <UitkCard border={rfrrID ? true : false}>
                  <div
                    className="CallToAction"
                    tabIndex={0}
                    role="button"
                    aria-expanded={isVisible}
                    onClick={toggle}
                    data-testid="expando-toggle"
                    onKeyDown={expandoKeyboardPress}
                  >
                    <UitkCardContentSection padded>
                      <UitkLayoutFlex>
                        <UitkLayoutFlexItem grow={1}>{rfrrID ? expandoTitle : normalTitle}</UitkLayoutFlexItem>
                        <UitkLayoutFlexItem alignSelf="center" grow={0}>
                          <UitkCardContentSection padded={false}>
                            <UitkIcon name={isVisible ? "expand_less" : "expand_more"} size={UitkIconSize.SMALL} />
                          </UitkCardContentSection>
                        </UitkLayoutFlexItem>
                      </UitkLayoutFlex>
                    </UitkCardContentSection>
                  </div>
                  <UitkCardContentSection
                    padded={["blockend", "inlinestart", "inlineend"]}
                    className={`${isVisible ? "" : "is-hidden"}`}
                  >
                    <UitkSpacing padding={{ blockend: "one", inline: "one" }}>
                      <div>
                        <ContainerContent classNames="SimpleContainer" includeTitle={false} />
                      </div>
                    </UitkSpacing>
                  </UitkCardContentSection>
                </UitkCard>
              </div>
            </UitkSpacing>
          </ViewSmall>
        )}

        {tabletWidth === "hidden" ? (
          <ViewMedium />
        ) : (
          <ViewMedium>
            <UitkSpacing padding={{ block: rfrrID ? "two" : "three" }}>
              <div className="SimpleContainer ToggleContainer">
                <UitkCard border={rfrrID ? true : false}>
                  <div
                    className="CallToAction"
                    tabIndex={0}
                    role="button"
                    aria-expanded={isVisible}
                    onClick={toggle}
                    data-testid="expando-toggle"
                    onKeyDown={expandoKeyboardPress}
                  >
                    <UitkCardContentSection padded>
                      <UitkLayoutFlex>
                        <UitkLayoutFlexItem grow={1}>{rfrrID ? expandoTitle : normalTitle}</UitkLayoutFlexItem>
                        <UitkLayoutFlexItem alignSelf="center" grow={0}>
                          <UitkCardContentSection padded={false}>
                            <UitkIcon name={isVisible ? "expand_less" : "expand_more"} size={UitkIconSize.SMALL} />
                          </UitkCardContentSection>
                        </UitkLayoutFlexItem>
                      </UitkLayoutFlex>
                    </UitkCardContentSection>
                  </div>
                  <UitkCardContentSection
                    padded={["blockend", "inlinestart", "inlineend"]}
                    className={`${isVisible ? "" : "is-hidden"}`}
                  >
                    <UitkSpacing padding={{ blockend: "one", inline: "one" }}>
                      <div>
                        <ContainerContent classNames="SimpleContainer" includeTitle={false} />
                      </div>
                    </UitkSpacing>
                  </UitkCardContentSection>
                </UitkCard>
              </div>
            </UitkSpacing>
          </ViewMedium>
        )}

        {desktopWidth === "hidden" ? (
          <ViewLarge />
        ) : (
          <ViewLarge>
            <UitkSpacing padding={{ block: rfrrID ? "two" : "three" }}>
              <div className="SimpleContainer ToggleContainer">
                <UitkCard border={rfrrID ? true : false}>
                  <div
                    className="CallToAction"
                    tabIndex={0}
                    role="button"
                    aria-expanded={isVisible}
                    onClick={toggle}
                    data-testid="expando-toggle"
                    onKeyDown={expandoKeyboardPress}
                  >
                    <UitkCardContentSection padded>
                      <UitkLayoutFlex>
                        <UitkLayoutFlexItem grow={1}>{rfrrID ? expandoTitle : normalTitle}</UitkLayoutFlexItem>
                        <UitkLayoutFlexItem alignSelf="center" grow={0}>
                          <UitkCardContentSection padded={false}>
                            <UitkIcon name={isVisible ? "expand_less" : "expand_more"} size={UitkIconSize.SMALL} />
                          </UitkCardContentSection>
                        </UitkLayoutFlexItem>
                      </UitkLayoutFlex>
                    </UitkCardContentSection>
                  </div>
                  <UitkCardContentSection
                    padded={["blockend", "inlinestart", "inlineend"]}
                    className={`${isVisible ? "" : "is-hidden"}`}
                  >
                    <UitkSpacing padding={{ blockend: "one", inline: "one" }}>
                      <div>
                        <ContainerContent classNames="SimpleContainer" includeTitle={false} />
                      </div>
                    </UitkSpacing>
                  </UitkCardContentSection>
                </UitkCard>
              </div>
            </UitkSpacing>
          </ViewLarge>
        )}
      </Viewport>
    );

    React.useEffect(() => {
      if (collapseAll === ToggleContainerState.ALL_EXPANDED) {
        setIsVisible(true);
      } else if (collapseAll === ToggleContainerState.ALL_COLLAPSED) {
        setIsVisible(false);
      }
    }, [collapseAll]);

    return (
      <ToggleSingleContainerContext.Provider value={{ modulesHaveBorder }}>
        {rfrrID ? (
          <IntersectionTracker linkName={impressionLinkName} referrerId={impressionRfrrID}>
            {toggleSingleContainerView}
          </IntersectionTracker>
        ) : (
          <>{toggleSingleContainerView}</>
        )}
      </ToggleSingleContainerContext.Provider>
    );
  }
);

export default ToggleSingleContainer;
